/**
 * Authenticated Layout
 *
 * Main layout for authenticated portal pages with sidebar and header.
 * Blueprint Task: 2.2.6
 */

import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useLocation, Link, useNavigate } from 'react-router-dom';

import { NotificationBell } from '../components/NotificationBell';
import { BrandWordmark } from '../components/shared/BrandWordmark';
import {
  HomeIcon,
  BriefcaseIcon,
  BuildingIcon,
  PackageIcon,
  FolderIcon,
  ReceiptIcon,
  ChatIcon,
  CalendarIcon,
  MenuIcon,
  XIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  PlusIcon,
  CogIcon,
  LogoutIcon,
} from '../components/shared/icons';
import { PORTAL_BRAND } from '../config/branding';
import { useDeadlineAlerts } from '../hooks/useDeadlineAlerts';
import { useAuth } from '../providers/AuthProvider';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Services', href: '/services', icon: BriefcaseIcon },
  { name: 'Entities', href: '/entities', icon: BuildingIcon },
  { name: 'Orders', href: '/orders', icon: PackageIcon },
  { name: 'Documents', href: '/documents', icon: FolderIcon },
  { name: 'Invoices', href: '/invoices', icon: ReceiptIcon },
  { name: 'Support', href: '/support', icon: ChatIcon },
  { name: 'Calendar', href: '/calendar', icon: CalendarIcon },
];

/**
 *
 */
export function AuthenticatedLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();
  const deadlineAlertCount = useDeadlineAlerts();
  const location = useLocation();
  const navigate = useNavigate();

  // Close user menu on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [userMenuOpen]);

  // Generate breadcrumbs from path
  const breadcrumbs = location.pathname
    .split('/')
    .filter(Boolean)
    .map((segment, index, array) => ({
      name: segment.charAt(0).toUpperCase() + segment.slice(1),
      href: '/' + array.slice(0, index + 1).join('/'),
      current: index === array.length - 1,
    }));

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--color-neutral-50))' }}>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 transform bg-surface shadow-xl transition-transform duration-200 ease-in-out lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <img src={PORTAL_BRAND.logoMarkUrl} alt={PORTAL_BRAND.companyName} className="h-8 w-8" />
            <span
              className="relative text-xl font-bold"
              style={{ color: 'hsl(var(--color-primary-600))' }}
            >
              <BrandWordmark />
              <span className="absolute -top-0.5 -right-1 text-[7px] font-medium" style={{ color: 'hsl(var(--color-neutral-400))' }} aria-hidden="true">TM</span>
            </span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="rounded-md p-2"
            style={{ color: 'hsl(var(--color-neutral-500))' }}
          >
            <XIcon className="h-6 w-6" />
          </button>
        </div>
        <nav className="mt-4 px-2">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium ${
                  isActive ? 'bg-primary-50' : ''
                }`
              }
              style={({ isActive }) => ({
                backgroundColor: isActive ? 'hsl(var(--color-primary-50))' : undefined,
                color: isActive
                  ? 'hsl(var(--color-primary-700))'
                  : 'hsl(var(--color-neutral-700))',
              })}
            >
              <item.icon className="h-5 w-5" />
              {item.name}
              {item.name === 'Calendar' && deadlineAlertCount > 0 && (
                <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-error-500 px-1.5 text-[10px] font-bold text-white">
                  {deadlineAlertCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col border-r bg-surface">
          <div className="flex h-16 items-center px-6">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src={PORTAL_BRAND.logoMarkUrl} alt="" className="h-7 w-7 rounded-md" />
              <span className="text-lg font-bold tracking-tight" style={{ color: 'hsl(var(--color-primary-700))' }}><BrandWordmark /></span>
            </Link>
          </div>
          <nav className="flex-1 space-y-1 px-3 py-4">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors"
                style={({ isActive }) => ({
                  backgroundColor: isActive ? 'hsl(var(--color-primary-50))' : undefined,
                  color: isActive
                    ? 'hsl(var(--color-primary-700))'
                    : 'hsl(var(--color-neutral-700))',
                })}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
                {item.name === 'Calendar' && deadlineAlertCount > 0 && (
                  <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-error-500 px-1.5 text-[10px] font-bold text-white">
                    {deadlineAlertCount}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="border-t p-4">
            <Link
              to="/order"
              className="flex w-full items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white"
              style={{ backgroundColor: 'hsl(var(--color-primary-600))' }}
            >
              <PlusIcon className="h-5 w-5" />
              New Service
            </Link>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header
          className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b bg-surface px-4 lg:px-6"
        >
          <button
            onClick={() => setSidebarOpen(true)}
            className="rounded-md p-2 lg:hidden"
            style={{ color: 'hsl(var(--color-neutral-500))' }}
          >
            <MenuIcon className="h-6 w-6" />
          </button>

          {/* Breadcrumbs */}
          <nav className="hidden items-center gap-2 text-sm sm:flex">
            {breadcrumbs.map((crumb, index) => (
              <span key={crumb.href} className="flex items-center gap-2">
                {index > 0 && (
                  <ChevronRightIcon
                    className="h-4 w-4"
                    style={{ color: 'hsl(var(--color-neutral-400))' }}
                  />
                )}
                {crumb.current ? (
                  <span style={{ color: 'hsl(var(--color-neutral-900))' }}>{crumb.name}</span>
                ) : (
                  <Link
                    to={crumb.href}
                    style={{ color: 'hsl(var(--color-neutral-500))' }}
                    className="hover:underline"
                  >
                    {crumb.name}
                  </Link>
                )}
              </span>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-4">
            {/* Notification bell */}
            <NotificationBell />

            {/* User menu dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen((prev) => !prev)}
                className="flex items-center gap-2 rounded-md p-2 transition-colors hover:bg-brand-neutral-200"
                style={{ color: 'hsl(var(--color-neutral-700))' }}
              >
                <div
                  className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium text-white"
                  style={{ backgroundColor: 'hsl(var(--color-primary-600))' }}
                >
                  {user?.name?.charAt(0).toUpperCase() ?? user?.email?.charAt(0).toUpperCase()}
                </div>
                <span className="hidden text-sm font-medium sm:block">
                  {user?.name ?? user?.email}
                </span>
                <ChevronDownIcon className={`hidden h-4 w-4 transition-transform sm:block ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown */}
              {userMenuOpen && (
                <div
                  className="absolute right-0 mt-1 w-48 origin-top-right rounded-md border bg-surface-elevated py-1 shadow-lg"
                  style={{
                    borderColor: 'hsl(var(--color-neutral-200))',
                    animation: 'fadeSlideDown 150ms ease-out',
                  }}
                >
                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate('/account');
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-brand-neutral-200"
                    style={{ color: 'hsl(var(--color-neutral-700))' }}
                  >
                    <CogIcon className="h-4 w-4" />
                    Account Settings
                  </button>
                  <div className="my-1 border-t" style={{ borderColor: 'hsl(var(--color-neutral-100))' }} />
                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      void logout();
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-brand-neutral-200"
                    style={{ color: 'hsl(var(--color-neutral-600))' }}
                  >
                    <LogoutIcon className="h-4 w-4" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>

        {/* Legacy legal footer; the company footer is global on Geaux File. */}
        {PORTAL_BRAND.id === 'eleet' ? <footer className="border-t border-brand-neutral-200 px-4 py-3 text-center text-xs text-brand-neutral-500 lg:pl-64">
          <a href={PORTAL_BRAND.termsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-brand-neutral-700">Terms</a>
          {' | '}
          <a href={PORTAL_BRAND.privacyUrl} target="_blank" rel="noopener noreferrer" className="hover:text-brand-neutral-700">Privacy</a>
        </footer> : null}
      </div>

    </div>
  );
}
